"use client";

import DrawerCloseIcon from "hec/assets/icons/drawer-close-icon";
import type * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { isEditing } from "@builder.io/sdk-react";

import { cn } from "~/lib/utils";

import { ScrollArea } from "./scroll-area";

const Drawer = ({
  // Default true preserves Vaul's body scale-back animation across the basket,
  // mobile nav, CIO filters, etc. Trade-off: the body `transform: scale(...)`
  // creates a stacking context that breaks `backdrop-filter`, so the overlay's
  // `backdrop-blur-sm` won't visibly render when scale-back is on. Callsites
  // that want blur over animation can opt out with `shouldScaleBackground={false}`.
  shouldScaleBackground = true,
  // Default false: Vaul otherwise paints `body { background-color: black }`
  // during scale-back so the bezel behind the scaled wrapper looks neutral.
  // Our real page background is white-ish, so the black flash reads as a
  // bug. Callsites that genuinely want the black backdrop can opt back in.
  setBackgroundColorOnScale = false,
  // Default false: lets users swipe anywhere on the drawer surface to dismiss
  // (iOS pattern). Vaul auto-distinguishes tap vs drag on form inputs in
  // most cases; specific elements can opt out with `data-vaul-no-drag`.
  // Pass `handleOnly={true}` plus a `<Drawer.Handle />` for surfaces that
  // need drag confined to a visible handle.
  handleOnly = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    setBackgroundColorOnScale={setBackgroundColorOnScale}
    handleOnly={handleOnly}
    {...props}
  />
);
Drawer.displayName = "Drawer";

const closeDrawerEvent = new Event("closeDrawer");

const DrawerTrigger: typeof DrawerPrimitive.Trigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose: typeof DrawerPrimitive.Close = DrawerPrimitive.Close;

const DrawerOverlay = ({
  className,
  "data-testid": dataTestId,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
  className?: string;
  "data-testid"?: string | undefined;
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn(
      // `data-[state=closed]:!pointer-events-none` is load-bearing, not polish.
      // This overlay is `fixed` full-screen at `z-50`, so while it is mounted it
      // sits over the entire page. It is supposed to unmount when the drawer
      // closes — but if the exit animation never completes it stays in the DOM
      // marked `data-state="closed" aria-hidden="true"`, invisible and still
      // swallowing every click. Shoppers report that as the page being "stuck":
      // it looks fine, nothing is loading, and nothing responds.
      //
      // Two Jam recordings (2026-08-15/16) each caught a click landing on a
      // closed, aria-hidden, full-screen overlay — the vaul one here and the
      // Radix dialog one in `components/ui/dialog.tsx`. Making the closed state
      // click-through means a stalled exit animation can no longer trap anyone,
      // regardless of WHY it stalled. See also SFCC-11675, which fixed one
      // cause (a dismiss dispatched during the enter animation) on the dialog.
      //
      // The `!` is load-bearing too: vaul's Overlay renders Radix's
      // DialogPrimitive.Overlay, which carries an unconditional inline
      // `style="pointer-events: auto"` while mounted — a non-important class
      // never beats an inline style, so without `!important` this guard is
      // inert in exactly the stall it exists for.
      "data-[state=open]:animate-overlay-show data-[state=closed]:animate-overlay-hide fixed left-0 top-0 z-50 h-full w-full bg-overlay backdrop-blur-sm data-[state=closed]:!pointer-events-none",
      className,
    )}
    data-testid={dataTestId || "drawer-DrawerOverlay"}
    {...props}
  />
);
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const drawerSizeMap = {
  default: "sm:max-w-screen-sm",
  narrow: "sm:max-w-[25rem]",
  wide: "sm:max-w-screen-md",
} as const;

type DrawerSize = keyof typeof drawerSizeMap;

/**
 * Undo vaul's pointer capture when a press lands on a native `<select>`
 * (ET-559).
 *
 * vaul's `onPress` calls `event.target.setPointerCapture()` on EVERY press in
 * the drawer — its SELECT / `data-vaul-no-drag` exclusions only run later, on
 * move/release, so they never stop the capture. Chrome drops it on pointerup;
 * Safari's native select menu swallows the pointerup, so the select keeps the
 * capture and every later click in the page is retargeted to it — the menu
 * re-opens on each checkbox/field click and the register form is unusable.
 *
 * vaul calls our `onPointerDown` BEFORE `onPress`, in the same synchronous
 * handler, so a microtask runs just after vaul has captured. Not
 * `stopPropagation`: that would also hide the press from Radix
 * DismissableLayer's document listener and break the next overlay dismiss.
 */
const releaseSelectPointerCapture = (e: React.PointerEvent<HTMLDivElement>) => {
  const target = e.target as Element;
  if (!target.closest?.("select")) return;
  const { pointerId } = e;
  queueMicrotask(() => {
    if (target.hasPointerCapture?.(pointerId)) {
      target.releasePointerCapture(pointerId);
    }
  });
};

const DrawerContent = ({
  className,
  children,
  size = "default",
  "data-testid": dataTestId,
  ref,
  onPointerDown,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
  className?: string;
  size?: DrawerSize;
  "data-testid"?: string | undefined;
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <DrawerPortal data-testid={dataTestId || "drawer-DrawerPortal"}>
    <DrawerOverlay data-testid={dataTestId || "drawer-DrawerOverlay"} />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        "data-[state=open]:animate-drawer-show-mobile data-[state=open]:lg:animate-drawer-show-desktop data-[state=closed]:animate-drawer-hide-mobile data-[state=closed]:lg:animate-drawer-hide-desktop bottom-0 left-0 z-50 w-full overflow-y-auto bg-white",
        drawerSizeMap[size],
        // The closed-state guard lives in the `fixed` branch only: that is the
        // shopper-facing full-screen-on-mobile layer whose stalled slide-out
        // parks it invisibly over the page (vaul Content is Radix's
        // DialogPrimitive.Content underneath — same stall exposure as the
        // overlay above; the `!` beats Radix's inline `pointer-events`). The
        // Builder-editing branch renders in-flow and never covers the page.
        isEditing()
          ? "relative mt-[-100%] h-full"
          : "fixed h-[100dvh] data-[state=closed]:!pointer-events-none",
        className,
      )}
      data-testid={dataTestId || "drawer-DrawerContent"}
      {...props}
      onPointerDown={e => {
        onPointerDown?.(e);
        releaseSelectPointerCapture(e);
      }}
    >
      <ScrollArea data-testid={dataTestId || "drawer-ScrollArea"}>
        {children}
      </ScrollArea>
    </DrawerPrimitive.Content>
  </DrawerPortal>
);
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  heading,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { heading?: string }) => (
  <div
    className={cn(
      heading
        ? "flex items-center justify-between border-b-3 border-primary p-4 pr-5"
        : "flex justify-end p-4 pr-5",
      className,
    )}
    {...props}
  >
    {heading && (
      <span className="font-grotesque text-sm font-medium text-primary">
        {heading}
      </span>
    )}
    <DrawerPrimitive.Close
      className="cursor-pointer p-1"
      onClick={() => document.dispatchEvent(closeDrawerEvent)}
    >
      <DrawerCloseIcon />
      <span className="sr-only">Close</span>
    </DrawerPrimitive.Close>
  </div>
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = ({
  className,
  "data-testid": dataTestId,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> & {
  className?: string;
  "data-testid"?: string | undefined;
  ref?: React.Ref<HTMLHeadingElement>;
}) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    data-testid={dataTestId || "drawer-DrawerTitle"}
    {...props}
  />
);
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = ({
  className,
  "data-testid": dataTestId,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> & {
  className?: string;
  "data-testid"?: string | undefined;
  ref?: React.Ref<HTMLParagraphElement>;
}) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    data-testid={dataTestId || "drawer-DrawerDescription"}
    {...props}
  />
);
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
